import styled from "styled-components";
import {useCallback, useRef, useState, useLayoutEffect, useEffect} from "react";
import {ReactSortable} from "react-sortablejs";
import {PopupBodyStyled, PopupHeaderStyled} from "../popup/PopupV5";
import {baseDistance, FLEXCENTER, fontSize, fontWeight, radius, zIndex} from "../styles/styles";
import {colorPalette} from "../styles/colorPalette";
import {ButtonV5} from "../button/ButtonV5";
import CirclePlusIcon from "../icons/circle-plus.svg?react";
import ChevronDownIcon from "../icons/chevron-down.svg?react";
import ChevronUpIcon from "../icons/chevron-up.svg?react";
import {useAppStore} from "../../../StateManager";
import {XyiconFeature} from "../../../generated/api/base";
import type {IModel} from "../../../data/models/Model";
import type {ISpotlightRule} from "../../../data/models/View";
import {DomUtils} from "../../../utils/dom/DomUtils";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {useClickOutside, useCloseOnKeyboardPress} from "../utils";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {disableHighlightingOnBody, enableHighlightingOnBody} from "../../../utils/interaction/HighlightDisablingUtils";
import {SpotlightFieldBasedRulesV5} from "./SpotlightFieldBasedRulesV5";
import {useSpotlightRules} from "./useSpotlightRules";
import {SpotlightConditionBasedRuleV5} from "./SpotlightConditonBasedRuleV5";

interface ISpotlightBodyContent {
	readonly feature: XyiconFeature;
	readonly items: IModel[];
	readonly openInSpaceEditor?: boolean;
}

export const SpotlightBodyContentV5 = (props: ISpotlightBodyContent) => {
	const {feature, items, openInSpaceEditor = false} = props;
	const appState = useAppStore((state) => state.appState);

	const _addRulePopupRef = useRef<HTMLDivElement>(null);
	const _addRuleButtonRef = useRef<HTMLDivElement>(null);
	const _rulesContainerRef = useRef<HTMLDivElement>(null);
	const _previousCursorStyleRef = useRef<string>("");
	const [showAddRulePopup, setShowAddRulePopup] = useState(false);
	const [popupPosition, setPopupPosition] = useState({top: 0, left: 0});
	const [activeSection, setActiveSection] = useState<XyiconFeature | null>(null);
	const [lastAddedRuleId, setLastAddedRuleId] = useState<string | null>(null);

	// Maintain expand/collapse state for all rules in parent (loaded from localStorage on mount)
	const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(() => {
		const states: Record<string, boolean> = {};
		// Load all spotlight expand states from localStorage
		Object.keys(localStorage).forEach((key) => {
			if (key.startsWith("spotlight-rule-expanded-")) {
				states[key] = localStorage.getItem(key) === "true";
			}
		});
		return states;
	});

	// Sync expandedStates to localStorage whenever it changes
	useEffect(() => {
		Object.entries(expandedStates).forEach(([key, value]) => {
			localStorage.setItem(key, String(value));
		});
	}, [expandedStates]);

	const SPOTLIGHT_SECTION_EXPANDED_LOCALSTORAGE_KEY = "spotlight-section-expanded";

	const [sectionExpanded, setSectionExpanded] = useState<boolean>(() => {
		try {
			const saved = localStorage.getItem(SPOTLIGHT_SECTION_EXPANDED_LOCALSTORAGE_KEY);
			return saved !== null ? JSON.parse(saved) : true;
		} catch {
			return true;
		}
	});

	const listViewSpotlightRules = useSpotlightRules({
		feature: openInSpaceEditor ? XyiconFeature.SpaceEditor : feature,
		appState,
		ruleType: "listViewSpotlightRules",
	});

	const xyiconSpotlightRules = useSpotlightRules({
		feature: openInSpaceEditor ? XyiconFeature.SpaceEditor : XyiconFeature.Xyicon,
		appState,
		ruleType: "xyiconSpotlightRules",
	});

	const boundarySpotlightRules = useSpotlightRules({
		feature: openInSpaceEditor ? XyiconFeature.SpaceEditor : XyiconFeature.Boundary,
		appState,
		ruleType: "boundarySpotlightRules",
	});

	const onResetRules = useCallback(() => {
		if (openInSpaceEditor) {
			xyiconSpotlightRules.resetRules();
			boundarySpotlightRules.resetRules();
		} else {
			listViewSpotlightRules.resetRules();
		}
	}, [openInSpaceEditor, xyiconSpotlightRules, boundarySpotlightRules, listViewSpotlightRules]);

	const onClearAllRules = useCallback(() => {
		if (openInSpaceEditor) {
			xyiconSpotlightRules.clearAllRules();
			boundarySpotlightRules.clearAllRules();
		} else {
			listViewSpotlightRules.clearAllRules();
		}
	}, [openInSpaceEditor, xyiconSpotlightRules, boundarySpotlightRules, listViewSpotlightRules]);

	// Callback to handle when a rule's expand state changes
	const onRuleExpandChange = useCallback(
		(ruleId: string, isExpanded: boolean, sectionRules: any) => {
			const ruleType = openInSpaceEditor ? "space" : "list";

			setExpandedStates((prevStates) => {
				const newStates = {...prevStates};

				if (!isExpanded) {
					// User collapsed a rule → collapse ALL rules
					sectionRules.rules.forEach((rule: ISpotlightRule) => {
						const storageKey = `spotlight-rule-expanded-${ruleType}-${rule.id}`;
						newStates[storageKey] = false;
					});
				} else {
					// User expanded a rule → collapse all others, expand only this one (one-at-a-time)
					sectionRules.rules.forEach((rule: ISpotlightRule) => {
						const storageKey = `spotlight-rule-expanded-${ruleType}-${rule.id}`;
						newStates[storageKey] = rule.id === ruleId;
					});
				}

				return newStates;
			});
		},
		[openInSpaceEditor],
	);

	const onShowAddRulePopup = useCallback(
		(e: React.MouseEvent, sectionFeature?: XyiconFeature) => {
			e.stopPropagation();
			setShowAddRulePopup(true);
			if (openInSpaceEditor && sectionFeature) {
				setActiveSection(sectionFeature);
			}
		},
		[openInSpaceEditor],
	);

	useLayoutEffect(() => {
		if (showAddRulePopup && _addRuleButtonRef.current) {
			const buttonRect = _addRuleButtonRef.current.getBoundingClientRect();
			setPopupPosition({
				top: buttonRect.bottom + 4,
				left: buttonRect.left,
			});
		}
	}, [showAddRulePopup]);

	const onHideAddRulePopup = useCallback(() => {
		setShowAddRulePopup(false);
		setActiveSection(null);
	}, []);

	const onAddConditionalRule = useCallback(
		(sectionFeature?: XyiconFeature, e?: React.MouseEvent) => {
			if (e) {
				e.stopPropagation();
			}
			let newRule;
			if (openInSpaceEditor && sectionFeature) {
				setActiveSection(sectionFeature);
				if (sectionFeature === XyiconFeature.Xyicon) {
					newRule = xyiconSpotlightRules.addRule("conditional");
				} else {
					newRule = boundarySpotlightRules.addRule("conditional");
				}
			} else {
				newRule = listViewSpotlightRules.addRule("conditional");
			}
			setShowAddRulePopup(false);
			if (newRule) {
				setLastAddedRuleId(newRule.id);
			}
		},
		[openInSpaceEditor, xyiconSpotlightRules, boundarySpotlightRules, listViewSpotlightRules],
	);

	const onAddFieldBasedRule = useCallback(
		(sectionFeature?: XyiconFeature, e?: React.MouseEvent) => {
			if (e) {
				e.stopPropagation();
			}
			let newRule;
			if (openInSpaceEditor && sectionFeature) {
				setActiveSection(sectionFeature);
				if (sectionFeature === XyiconFeature.Xyicon) {
					newRule = xyiconSpotlightRules.addRule("field");
				} else {
					newRule = boundarySpotlightRules.addRule("field");
				}
			} else {
				newRule = listViewSpotlightRules.addRule("field");
			}
			setShowAddRulePopup(false);
			if (newRule) {
				setLastAddedRuleId(newRule.id);
			}
		},
		[openInSpaceEditor, xyiconSpotlightRules, boundarySpotlightRules, listViewSpotlightRules],
	);

	const toggleSections = useCallback(() => {
		const newExpandedState = !sectionExpanded;
		setSectionExpanded(newExpandedState);
		try {
			localStorage.setItem(SPOTLIGHT_SECTION_EXPANDED_LOCALSTORAGE_KEY, JSON.stringify(newExpandedState));
		} catch (error) {
			console.warn("Failed to save spotlight section state to localStorage:", error);
		}
	}, [sectionExpanded]);

	useClickOutside([_addRulePopupRef], onHideAddRulePopup, "mousedown", []);
	useCloseOnKeyboardPress(onHideAddRulePopup, KeyboardListener.KEY_ESCAPE, [showAddRulePopup], {
		enabled: showAddRulePopup,
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	const onDragStart = useCallback(() => {
		_previousCursorStyleRef.current = document.body.style.cursor;
		document.body.style.cursor = "grabbing";
		disableHighlightingOnBody();
	}, []);

	const onDragEnd = useCallback(() => {
		document.body.style.cursor = _previousCursorStyleRef.current;
		enableHighlightingOnBody();
	}, []);

	// Auto-scroll to the newly added rule
	useEffect(() => {
		if (lastAddedRuleId && _rulesContainerRef.current) {
			const ruleElement = _rulesContainerRef.current.querySelector(`[data-rule-id="${lastAddedRuleId}"]`);
			if (ruleElement) {
				DomUtils.scrollIntoViewIfNeeded(ruleElement as HTMLElement, true);
				// Clear the lastAddedRuleId after scrolling

				setLastAddedRuleId(null);
			}
		}
	}, [lastAddedRuleId]);

	const renderIndividualRule = (rule: any, sectionRules: any, sectionFeature?: XyiconFeature) => {
		const fieldFeature = openInSpaceEditor && sectionFeature ? sectionFeature : feature;
		const ruleType = openInSpaceEditor ? "space" : "list";
		const storageKey = `spotlight-rule-expanded-${ruleType}-${rule.id}`;
		const isExpanded = expandedStates[storageKey] ?? true; // Default to expanded if not in state

		if (rule.ruleType === "conditional") {
			return (
				<SpotlightConditionBasedRuleV5
					key={rule.id}
					features={[fieldFeature]}
					rule={rule}
					onRuleUpdate={sectionRules.updateRule}
					onRuleDelete={sectionRules.removeRule}
					openInSpaceEditor={openInSpaceEditor}
					onExpandChange={(ruleId, isExpanded) => onRuleExpandChange(ruleId, isExpanded, sectionRules)}
					isExpanded={isExpanded}
				/>
			);
		} else if (rule.ruleType === "field") {
			return (
				<SpotlightFieldBasedRulesV5
					key={rule.id}
					rule={rule}
					feature={fieldFeature}
					items={items}
					onRuleUpdate={sectionRules.updateRule}
					onRuleDelete={sectionRules.removeRule}
					openInSpaceEditor={openInSpaceEditor}
					onExpandChange={(ruleId, isExpanded) => onRuleExpandChange(ruleId, isExpanded, sectionRules)}
					isExpanded={isExpanded}
				/>
			);
		}
		return null;
	};

	const renderConditionOrFieldRuleOption = (sectionFeature?: XyiconFeature) => {
		return (
			<>
				<div
					onClick={(e) => onAddConditionalRule(sectionFeature, e)}
					className="rule-option-condition"
				>
					<div>Conditions</div>
					<span>Color item based on conditions</span>
				</div>
				<div
					onClick={(e) => onAddFieldBasedRule(sectionFeature, e)}
					className="rule-option-field"
				>
					<div>Select Field</div>
					<span>Color item based on a selected field</span>
				</div>
			</>
		);
	};

	const renderSpotlightSection = (sectionFeature: XyiconFeature) => {
		const isXyicon = sectionFeature === XyiconFeature.Xyicon;
		const sectionRules = isXyicon ? xyiconSpotlightRules : boundarySpotlightRules;
		const sectionHasRules = sectionRules.ruleCount > 0;
		return (
			<>
				{sectionHasRules ? (
					<>
						<ReactSortable
							list={sectionRules.rules}
							setList={sectionRules.reorderRules}
							className="rules-sortable"
							animation={150}
							easing="cubic-bezier(.17,.67,.83,.67)"
							ghostClass="sortable-ghost-class__rules"
							dragClass="sortable-drag-class__rules"
							chosenClass="sortable-chosen__rules"
							handle=".drag-handle"
							forceFallback={true}
							onStart={onDragStart}
							onEnd={onDragEnd}
						>
							{sectionRules.rules.map((rule) => renderIndividualRule(rule, sectionRules, sectionFeature))}
						</ReactSortable>
						<div
							className="add-rule-btn-container"
							ref={_addRuleButtonRef}
						>
							<ButtonV5
								label="Add Spotlight Rule"
								type="tertiary"
								onClick={(e) => onShowAddRulePopup(e, sectionFeature)}
								className="add-condition-btn"
							>
								<CirclePlusIcon />
							</ButtonV5>
							{showAddRulePopup && activeSection === sectionFeature && (
								<DomPortal destination={appState.app.modalContainer}>
									<AddRulePopupStyled
										ref={_addRulePopupRef}
										style={{
											top: `${popupPosition.top}px`,
											left: `${popupPosition.left}px`,
										}}
									>
										{renderConditionOrFieldRuleOption(sectionFeature)}
									</AddRulePopupStyled>
								</DomPortal>
							)}
						</div>
					</>
				) : (
					<div className="rule-options-container">{renderConditionOrFieldRuleOption(sectionFeature)}</div>
				)}
			</>
		);
	};

	const renderHeader = (
		name: string,
		ruleCount: number,
		isExpanded: boolean,
		hasUnsavedChanges?: boolean,
		onReset?: () => void,
		onClearAll?: () => void,
	) => {
		const handleResetClick = (e: React.MouseEvent) => {
			e.stopPropagation();
			if (onReset) {
				onReset();
			} else {
				onResetRules();
			}
		};

		const handleClearAllClick = (e: React.MouseEvent) => {
			e.stopPropagation();
			if (onClearAll) {
				onClearAll();
			} else {
				onClearAllRules();
			}
		};

		return (
			<SpotlightHeaderStyled
				className="header"
				$isSmallPopup={true}
			>
				<div className="name-count-container">
					<div className="name">{name}</div>
					<div className="rule-count">{ruleCount}</div>
				</div>
				{isExpanded && (
					<div className="clear-reset-buttons">
						<ButtonV5
							label="Reset"
							onClick={handleResetClick}
							className="naked"
							type="tertiary"
							disabled={!hasUnsavedChanges}
						/>
						<ButtonV5
							label="Clear All"
							onClick={handleClearAllClick}
							className="naked"
							type="tertiary"
							disabled={ruleCount === 0}
						/>
					</div>
				)}
			</SpotlightHeaderStyled>
		);
	};

	return (
		<SpotlightContentContainer>
			{!openInSpaceEditor && renderHeader("Spotlight", listViewSpotlightRules.ruleCount, true, listViewSpotlightRules.hasUnsavedChanges)}
			<SpotlightBodyStyled
				className="body"
				ref={_rulesContainerRef}
			>
				{openInSpaceEditor ? (
					<>
						<SectionContainer $sectionExpanded={sectionExpanded}>
							<div
								onClick={toggleSections}
								className="xyicon-section-header"
							>
								<div className="section-title">
									{sectionExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
									{renderHeader(
										"Xyicon Spotlight",
										xyiconSpotlightRules.ruleCount,
										sectionExpanded,
										xyiconSpotlightRules.hasUnsavedChanges,
										xyiconSpotlightRules.resetRules,
										xyiconSpotlightRules.clearAllRules,
									)}
								</div>
							</div>
							{sectionExpanded && <SectionContent>{renderSpotlightSection(XyiconFeature.Xyicon)}</SectionContent>}
						</SectionContainer>
						<SectionContainer $sectionExpanded={sectionExpanded}>
							<div
								onClick={toggleSections}
								className="boundary-section-header"
							>
								<div className="section-title">
									{!sectionExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
									{renderHeader(
										"Boundary Spotlight",
										boundarySpotlightRules.ruleCount,
										!sectionExpanded,
										boundarySpotlightRules.hasUnsavedChanges,
										boundarySpotlightRules.resetRules,
										boundarySpotlightRules.clearAllRules,
									)}
								</div>
							</div>
							{!sectionExpanded && <SectionContent>{renderSpotlightSection(XyiconFeature.Boundary)}</SectionContent>}
						</SectionContainer>
					</>
				) : (
					<>
						{listViewSpotlightRules.ruleCount > 0 ? (
							<>
								<ReactSortable
									list={listViewSpotlightRules.rules}
									setList={listViewSpotlightRules.reorderRules}
									className="rules-sortable"
									animation={150}
									easing="cubic-bezier(.17,.67,.83,.67)"
									ghostClass="sortable-ghost-class__rules"
									dragClass="sortable-drag-class__rules"
									chosenClass="sortable-chosen__rules"
									handle=".drag-handle"
									forceFallback={true}
									onStart={onDragStart}
									onEnd={onDragEnd}
								>
									{listViewSpotlightRules.rules.map((rule) => renderIndividualRule(rule, listViewSpotlightRules))}
								</ReactSortable>
								<div
									className="add-rule-btn-container"
									ref={_addRuleButtonRef}
								>
									<ButtonV5
										label="Add Spotlight Rule"
										type="tertiary"
										onClick={(e) => onShowAddRulePopup(e)}
										className="add-condition-btn"
									>
										<CirclePlusIcon />
									</ButtonV5>
									{showAddRulePopup && (
										<DomPortal destination={appState.app.modalContainer}>
											<AddRulePopupStyled
												ref={_addRulePopupRef}
												style={{
													top: `${popupPosition.top}px`,
													left: `${popupPosition.left}px`,
												}}
											>
												{renderConditionOrFieldRuleOption()}
											</AddRulePopupStyled>
										</DomPortal>
									)}
								</div>
							</>
						) : (
							<div className="rule-options-container">{renderConditionOrFieldRuleOption()}</div>
						)}
					</>
				)}
			</SpotlightBodyStyled>
		</SpotlightContentContainer>
	);
};

const SpotlightBodyStyled = styled(PopupBodyStyled)`
	padding: 0px;
`;
const SpotlightHeaderStyled = styled(PopupHeaderStyled)`
	width: 100%;
	padding: ${baseDistance.sm} ${baseDistance.md};
`;

const SpotlightContentContainer = styled.div`
	display: flex;
	flex-direction: column;

	.rules-sortable {
		display: flex;
		flex-direction: column;
		gap: 8px;
		max-height: 472px;
		overflow-y: auto;
		overflow-x: hidden;
	}

	.rules-container {
		display: flex;
		flex-direction: column;
		gap: ${baseDistance.sm};
	}

	.drag-handle {
		display: flex;

		svg.drag-icon {
			width: 16px;
			height: 16px;
			color: ${colorPalette.gray.c700Dark};

			&:hover {
				cursor: grab;
			}
		}
	}

	.sortable-chosen__rules:not(.sortable-drag-class__rules) {
		border: 2px dashed ${colorPalette.primary.c500Primary};
		margin: ${baseDistance.sm};
		background: transparent;
		border-radius: ${radius.sm};

		& * {
			visibility: hidden;
		}

		&::before,
		&::after {
			display: none;
		}
	}

	.sortable-drag-class__rules {
		opacity: 1 !important;
		box-shadow: 0px 10px 10px 0 #0000001a;
		border: none;
		border-radius: ${radius.sm};
		background-color: ${colorPalette.gray.c100};
	}

	.name-count-container {
		display: flex;
		align-items: center;
		gap: ${baseDistance.sm};
	}

	.clear-reset-buttons {
		display: flex;
		align-items: center;
		gap: ${baseDistance.xs};

		.Button.disabled {
			.label {
				color: ${colorPalette.gray.c300};
			}
		}
	}

	.name {
		font-weight: ${fontWeight.bold};
		font-size: ${fontSize.lg};
	}

	.rule-count {
		${FLEXCENTER};
		min-width: 16px;
		min-height: 16px;
		border-radius: ${radius.xs};
		padding: ${baseDistance.xxs};
		background-color: ${colorPalette.primary.c500Primary};
		color: ${colorPalette.white};
		font-weight: ${fontWeight.normal};
		font-size: ${fontSize.sm};
	}

	.rule-options-container {
		display: flex;
		flex-direction: column;
		gap: 24px;
		padding: ${baseDistance.md} 0;

		span {
			font-size: ${fontSize.sm};
			color: ${colorPalette.gray.c700Dark};
			font-weight: ${fontWeight.normal};
		}
	}

	.rule-option-condition,
	.rule-option-field {
		height: 56px;
		display: flex;
		flex-direction: column;
		gap: ${baseDistance.sm};
		padding: ${baseDistance.sm};
		margin: 0 ${baseDistance.md};
		border-radius: ${radius.sm};
		cursor: pointer;

		&:hover {
			background-color: ${colorPalette.gray.c200Light};
		}

		span {
			height: 16px;
		}
	}

	.add-rule-btn-container {
		display: flex;
		justify-content: flex-start;
		position: relative;
		z-index: 1;
		margin: 24px ${baseDistance.md};
	}
`;

const AddRulePopupStyled = styled.div`
	position: fixed;
	background-color: ${colorPalette.white};
	border-radius: ${radius.md};
	box-shadow: 0px 4px 20px 0px #00000033;
	padding: ${baseDistance.sm};
	z-index: ${zIndex.createPanel + 1000};
	min-width: 240px;
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.sm};

	.rule-option-condition,
	.rule-option-field {
		display: flex;
		flex-direction: column;
		gap: ${baseDistance.xs};
		padding: ${baseDistance.sm};
		border-radius: ${radius.sm};
		cursor: pointer;

		&:hover {
			background-color: ${colorPalette.gray.c200Light};
		}

		div {
			font-size: ${fontSize.md};
			font-weight: ${fontWeight.bold};
			color: ${colorPalette.gray.c950};
		}

		span {
			font-size: ${fontSize.sm};
			color: ${colorPalette.gray.c700Dark};
			font-weight: ${fontWeight.normal};
		}
	}
`;

const SectionContainer = styled.div<{$sectionExpanded: boolean}>`
	display: flex;
	flex-direction: column;
	border-radius: ${radius.md};
	overflow: visible;
	position: relative;

	.xyicon-section-header {
		border-radius: ${radius.md} ${radius.md} 0 0;
		background-color: ${(props) => !props.$sectionExpanded && colorPalette.primary.c50};
	}

	.boundary-section-header {
		border-radius: 0 0 ${radius.md} ${radius.md};
		background-color: ${(props) => props.$sectionExpanded && colorPalette.primary.c50};
	}

	.section-title {
		padding: ${baseDistance.sm} ${baseDistance.md};
		width: 100%;
		height: 40px;
		display: flex;
		align-items: center;
		gap: ${baseDistance.sm};
		font-weight: ${fontWeight.bold};
		font-size: ${fontSize.md};
		color: ${colorPalette.gray.c950};
		align-content: space-between;

		${SpotlightHeaderStyled} {
			padding: 0;
		}

		svg {
			width: 16px;
			height: 16px;
			color: ${colorPalette.gray.c700Dark};
		}

		&:hover {
			cursor: pointer;
		}
	}
`;

const SectionContent = styled.div`
	position: relative;
	min-height: 112px;

	.rules-sortable {
		max-height: 424px;
	}
`;
